/**
 * Barreira final do horário.
 *
 * Módulo puro, sem IO e sem chamadas ao fornecedor. Recebe as paragens já
 * atribuídas a dias e devolve a lista final que respeita:
 *  - a janela do próprio dia (nunca uma hora global),
 *  - os horários de abertura reais no dia da semana correto,
 *  - o bloco rígido de partida do último dia.
 *
 * Regra: paragens obrigatórias (`required`) nunca são removidas — são
 * reordenadas para o primeiro encaixe possível. Apenas paragens opcionais
 * podem sair. Nada com nome de local entra no trace.
 */

import {
  findWindow,
  weekdayFor,
  type DayWindow,
} from "./day-windows";
import { periodsToDaySlots, type DaySlot, type OpeningPeriod } from "./place-availability";
import { classifyEveningSuitability } from "./evening-suitability";

export type ValidatableStop = {
  /** Identificador estável (placeId ou índice serializado). */
  id: string;
  day: number;
  /** Duração da visita em minutos. */
  visitMinutes?: number;
  /** Deslocação até esta paragem, em minutos. */
  transitMinutes?: number;
  /** Horários habituais do Google Places, quando conhecidos. */
  openingPeriods?: OpeningPeriod[] | null;
  /** Paragem obrigatória (pedido explícito, local fixado). */
  required?: boolean;
  /** Experiência confirmadamente noturna. */
  evening?: boolean;
  /** Tipos do Places — classificam interior/exterior sem horários. */
  placeTypes?: string[];
  placePrimaryType?: string;
  /** Categoria textual (fallback da classificação). */
  category?: string;
  /** Nome apenas para classificação; nunca entra no trace. */
  name?: string;
};

/**
 * Sem horários confirmados, um interior (museu, palácio, igreja, aquário…)
 * nunca pode ser agendado como se estivesse aberto à noite: assume-se um
 * horário conservador. Exteriores (miradouros, parques, praças) ficam livres.
 */
const UNKNOWN_HOURS_INDOOR_LATEST_START = 17 * 60;
const UNKNOWN_HOURS_INDOOR_LATEST_END = 18 * 60 + 30;

/** Limite conservador de fim de visita quando não há horários confirmados. */
export function conservativeEndCap(stop: ValidatableStop): number | null {
  if (stop.openingPeriods?.length) return null;
  // Sem qualquer sinal de tipo não há classificação possível: não se inventa
  // um horário para uma paragem que não sabemos o que é.
  const hasSignal =
    Boolean(stop.placePrimaryType) || (stop.placeTypes?.length ?? 0) > 0 || Boolean(stop.category);
  if (!hasSignal) return null;
  const level = classifyEveningSuitability({
    name: stop.name ?? "",
    placeTypes: stop.placeTypes,
    placePrimaryType: stop.placePrimaryType,
    category: stop.category,
    openingPeriods: stop.openingPeriods ?? null,
  });
  // "outdoor_view" (exterior) continua livre; interiores ficam limitados.
  return level === "daytime_only" ? UNKNOWN_HOURS_INDOOR_LATEST_END : null;
}

function latestStartFor(stop: ValidatableStop, visit: number): number | null {
  const cap = conservativeEndCap(stop);
  if (cap == null) return null;
  return Math.min(UNKNOWN_HOURS_INDOOR_LATEST_START, cap - visit);
}


export type DroppedReason =
  | "closed_on_day"
  | "no_time_left_in_window"
  | "after_departure_block";

export type ScheduleValidationTrace = {
  /** Paragens recebidas antes da barreira. */
  inputStops: number;
  /** Paragens mantidas no horário final. */
  keptStops: number;
  /** Paragens reordenadas dentro do próprio dia. */
  reorderedStops: number;
  /** Paragens opcionais removidas, por motivo. */
  droppedStops: number;
  droppedByReason: Record<DroppedReason, number>;
  /** Paragens obrigatórias que não encaixam em janela nenhuma. */
  unplaceableRequired: number;
  /** Dias avaliados. */
  days: number;
};

export type ScheduleValidationResult = {
  stops: ValidatableStop[];
  trace: ScheduleValidationTrace;
};

const DEFAULT_VISIT_MINUTES = 45;
const DEFAULT_TRANSIT_MINUTES = 10;
/** Sobreposição mínima com o horário de abertura para a visita valer a pena. */
const MIN_OPEN_OVERLAP = 20;

function slotsFor(stop: ValidatableStop): DaySlot[] {
  return periodsToDaySlots(stop.openingPeriods);
}

/** A paragem está aberta no intervalo [from, from+duration) do dia da semana? */
function opensDuring(
  slots: DaySlot[],
  weekday: number,
  from: number,
  duration: number,
): boolean {
  if (!slots.length) return true; // horários desconhecidos: não inventamos fecho
  const to = from + duration;
  for (const slot of slots) {
    if (slot.day !== weekday) continue;
    const overlap = Math.min(slot.endMinutes, to) - Math.max(slot.startMinutes, from);
    if (overlap >= Math.min(duration, MIN_OPEN_OVERLAP)) return true;
  }
  return false;
}

/** Aberta em algum momento desse dia da semana? */
function opensAnyTime(slots: DaySlot[], weekday: number): boolean {
  if (!slots.length) return true;
  return slots.some((s) => s.day === weekday);
}

/**
 * Reordena e, se necessário, remove paragens opcionais para que o horário
 * respeite as janelas por dia e os horários de abertura.
 */
export function validateSchedule(input: {
  stops: ValidatableStop[];
  dayWindows: DayWindow[];
  defaultVisitMinutes?: number;
  /**
   * Minutos já comprometidos nesse dia por blocos que não são visitas
   * (almoço, jantar, pequeno-almoço, chegada, check-out). Sem esta reserva a
   * agenda real transbordava a janela e produzia paragens à meia-noite.
   */
  reservedMinutesByDay?: Record<number, number>;
}): ScheduleValidationResult {
  const visitDefault = Math.max(5, input.defaultVisitMinutes ?? DEFAULT_VISIT_MINUTES);

  const trace: ScheduleValidationTrace = {
    inputStops: input.stops.length,
    keptStops: 0,
    reorderedStops: 0,
    droppedStops: 0,
    droppedByReason: {
      closed_on_day: 0,
      no_time_left_in_window: 0,
      after_departure_block: 0,
    },
    unplaceableRequired: 0,
    days: input.dayWindows.length,
  };

  const kept: ValidatableStop[] = [];
  const days = [...new Set(input.stops.map((s) => s.day))].sort((a, b) => a - b);

  for (const day of days) {
    const window = findWindow(input.dayWindows, day);
    const dayStops = input.stops.filter((s) => s.day === day);
    if (!window) {
      // Sem janela conhecida não há critério para cortar: mantém tudo.
      kept.push(...dayStops);
      continue;
    }
    const weekday = weekdayFor(window);
    const reserved = Math.max(0, input.reservedMinutesByDay?.[day] ?? 0);
    const usableEndMinutes = Math.max(window.startMinutes, window.endMinutes - reserved);

    const pending = [...dayStops];
    const placed: ValidatableStop[] = [];
    let cursor = window.startMinutes;

    while (pending.length) {
      let chosenIndex = -1;
      let chosenStart = 0;
      for (let i = 0; i < pending.length; i++) {
        const stop = pending[i];
        const slots = slotsFor(stop);
        const visit = Math.max(5, stop.visitMinutes ?? visitDefault);
        const transit = placed.length === 0 ? 0 : stop.transitMinutes ?? DEFAULT_TRANSIT_MINUTES;
        let start = cursor + transit;
        // Empurra para o horário de abertura mais próximo desse dia.
        if (slots.length) {
          const candidates = slots
            .filter((s) => s.day === weekday && s.endMinutes - Math.max(s.startMinutes, start) >= Math.min(visit, MIN_OPEN_OVERLAP))
            .map((s) => Math.max(s.startMinutes, start))
            .sort((a, b) => a - b);
          if (!candidates.length) continue;
          start = candidates[0];
        }
        if (start + visit > usableEndMinutes) continue;
        if (!opensDuring(slots, weekday, start, visit)) continue;
        // Sem horários confirmados, um interior nunca fica agendado à noite.
        const latestStart = latestStartFor(stop, visit);
        if (latestStart != null && start > latestStart) continue;

        // Preferência: primeiro encaixe possível; empate favorece obrigatórias
        // e adia as paragens noturnas para o fim do dia.
        const penalty = (stop.evening ? 1 : 0) * 10_000 - (stop.required ? 1 : 0) * 5;
        const score = start + penalty;
        if (chosenIndex === -1 || score < chosenStart) {
          chosenIndex = i;
          chosenStart = score;
        }
      }
      if (chosenIndex === -1) break;
      const stop = pending.splice(chosenIndex, 1)[0];
      const slots = slotsFor(stop);
      const visit = Math.max(5, stop.visitMinutes ?? visitDefault);
      const transit = placed.length === 0 ? 0 : stop.transitMinutes ?? DEFAULT_TRANSIT_MINUTES;
      let start = cursor + transit;
      if (slots.length) {
        const candidates = slots
          .filter((s) => s.day === weekday && s.endMinutes > Math.max(s.startMinutes, start))
          .map((s) => Math.max(s.startMinutes, start))
          .sort((a, b) => a - b);
        if (candidates.length) start = candidates[0];
      }
      cursor = start + visit;
      placed.push(stop);
    }

    // Sobras: não couberam na janela do dia ou estão fechadas nesse dia.
    for (const stop of pending) {
      const slots = slotsFor(stop);
      const reason: DroppedReason = !opensAnyTime(slots, weekday)
        ? "closed_on_day"
        : window.endSource === "departure_logistics"
          ? "after_departure_block"
          : "no_time_left_in_window";
      if (stop.required) {
        // Obrigatórias nunca são removidas: ficam no fim do dia, sinalizadas.
        trace.unplaceableRequired += 1;
        placed.push(stop);
        continue;
      }
      trace.droppedStops += 1;
      trace.droppedByReason[reason] += 1;
    }

    const originalOrder = dayStops.map((s) => s.id);
    placed.forEach((stop, index) => {
      if (originalOrder[index] !== stop.id) trace.reorderedStops += 1;
    });
    kept.push(...placed);
  }

  trace.keptStops = kept.length;
  return { stops: kept, trace };
}
